type useRulesReturn = {
  required: (field: string) => (value: string) => boolean | string,
  hasLettersOrNumbers: (field: string) => (value: string) => boolean | string,
  hasSearchCharactersOrEmpty: (field: string) => (value: string) => boolean | string,
  maxLength: (maxLength: number, field: string) => (value: string) => boolean | string,
  minLength: (minLength: number, field: string) => (value: string) => boolean | string,
  positive: (field: string) => (value: number) => boolean | string,
  greaterThan: (min: number, field: string) => (value: number) => boolean | string,
  email: (field: string) => (value: string) => boolean | string,
  isNotEmail: (field: string) => (value: string) => boolean | string,
  validFullUrl: (field: string) => (value: string) => boolean | string,
  notNumbersOnly: (field: string) => (value: string) => boolean | string,
  validUrl: (field: string) => (value: string) => boolean | string,
  allowedExtensions: (extensions: Array<string>) => (file: File) => boolean | string,
  valuesMatch: (field: string, value: string) => (value: string) => boolean | string,
  isNotEmpty: (field: string) => (value: string) => boolean | string,
  URL_REGEX: RegExp,
  EMAIL_REGEX: RegExp,
}

const useRules: () => useRulesReturn = () => {
  const INVALID_CHARACTERS_REGEX = /[-\s+!~^()[\]\\:"/]/g
  const NO_LETTERS_AND_NUMBERS_REGEX = /[^a-zA-Z0-9]/g

  const URL_REGEX = /((?:(?:http|https|Http|Https|rtsp|Rtsp):\/\/)?(?:(?:[a-zA-Z0-9$\-_\.\+\!\*\'\(\),;\?\&\=]|(?:%[a-fA-F0-9]{2})){1,64}(?::(?:[a-zA-Z0-9$\-_\.\+\!\*\'\(\),;\?\&\=]|(?:%[a-fA-F0-9]{2})){1,25})?\@)?((?:(?:[a-zA-Z0-9][a-zA-Z0-9\-]{0,63}\.)+(?:(?:aero|arpa|asia|a[cdefgilmnoqrstuwxz])|(?:biz|b[abdefghijmnorstvwyz])|(?:cat|com|coop|c[acdfghiklmnoruvxyz])|d[ejkmoz]|(?:edu|e[cegrstu])|f[ijkmor]|(?:gov|g[abdefghilmnpqrstuwy])|h[kmnrtu]|(?:info|int|i[delmnoqrst])|(?:jobs|j[emop])|k[eghimnrwyz]|l[abcikrstuvy]|(?:mil|mobi|museum|m[acdghklmnopqrstuvwxyz])|(?:name|net|n[acefgilopruz])|(?:org|om)|(?:pro|p[aefghklmnrstwy])|qa|r[eouw]|s[abcdeghijklmnortuvyz]|(?:tel|travel|t[cdfghjklmnoprtvwz])|u[agkmsyz]|v[aceginu]|w[fs]|y[etu]|z[amw]))|(?:(?:25[0-5]|2[0-4][0-9]|[0-1][0-9]{2}|[1-9][0-9]|[1-9])\.(?:25[0-5]|2[0-4][0-9]|[0-1][0-9]{2}|[1-9][0-9]|[1-9]|0)\.(?:25[0-5]|2[0-4][0-9]|[0-1][0-9]{2}|[1-9][0-9]|[1-9]|0)\.(?:25[0-5]|2[0-4][0-9]|[0-1][0-9]{2}|[1-9][0-9]|[0-9])))(?::\d{1,5})?)(\/(?:(?:[a-zA-Z0-9;\/?\:@&=\#\~\-\.\+\!\*\'\(\),_])|(?:%[a-fA-F0-9]{2}))*?)?(?:\b|$)/i
  const EMAIL_REGEX = /^(?!.*:\/\/).+@.+\..+$/
  const ALPHANUMERIC_REGEX = /^[a-zA-Z0-9\s]*$/
  const WHITESPACES_REGEX = /^[\s\S]*\S[\s\S]*$/
  const getLettersAndNumbers = (value: string): string | null => value?.replace(NO_LETTERS_AND_NUMBERS_REGEX, '') || null
  const getSearchCharacters = (value: string): string | null => value?.replace(INVALID_CHARACTERS_REGEX, '') || null

  const required = (field: string) => {
    return (value: string) => !!value || `Please enter your ${field}`
  }

  const hasLettersOrNumbers = () => {
    return (value: string) => (!value || !!getLettersAndNumbers(value)) || 'Please enter at least 1 letter or number'
  }

  const hasSearchCharactersOrEmpty = () => {
    return (value: string) => (!value || !!getSearchCharacters(value)) || 'Please enter a least 1 valid character'
  }

  const maxLength = (maxLength: number, field: string) => {
    return (value: string) => !value || value?.length <= maxLength || `The field ${field} can't have more than ${maxLength} characters`
  }

  const minLength = (minLength: number, field: string) => {
    return (value: string) => value?.length >= minLength || `${field} can't have more than ${minLength} characters.`
  }

  const positive = (field: string) => {
    return (value: number) => (!!value && value >= 0) || `${field} must be positive`
  }

  const greaterThan = (min: number, field: string) => {
    return (value: number) => (!!value && value > min) || `${field} must be greater than ${min}`
  }

  const email = () => {
    return (value: string) => EMAIL_REGEX.test(value) || !value || 'Please enter a valid e-mail address'
  }

  const isAlphanumeric = () => {
    return (value: string) => ALPHANUMERIC_REGEX.test(value) || 'Please enter an alphanumeric value'
  }

  const hasNoWhitespaces = () => {
    return (value: string) => WHITESPACES_REGEX.test(value) || 'Please try entering a character other than whitespace.'
  }

  const isNotEmail = (field: string) => {
    return (value: string) => !/.+@.+\..+/.test(value) || `${field} cannot be an email`
  }

  const validFullUrl = () => {
    return (value: string) => (!value || /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00A1-\uFFFF0-9]-*)*[a-z\u00A1-\uFFFF0-9]+)(?:\.(?:[a-z\u00A1-\uFFFF0-9]-*)*[a-z\u00A1-\uFFFF0-9]+)*(?:\.(?:[a-z\u00A1-\uFFFF]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(value)) ||
      'Please enter a valid URL'
  }

  const notNumbersOnly = (field: string) => {
    return (value: string) => !/^[0-9]*$/.test(value) || `${field} cannot be numbers only`
  }

  const validUrl = () => {
    return (value: string) => (!value || (URL_REGEX.test(value) && !EMAIL_REGEX.test(value))) || 'Please enter a valid URL'
  }

  const allowedExtensions = (extensions: Array<string>) => {
    return (file: File | Array<File>) => {
      const hasAllowedExtension = (file: File) => extensions.some(ext => file?.name?.endsWith(ext))
      return (file && ((Array.isArray(file) && file.every(hasAllowedExtension)) || hasAllowedExtension(file as File))) || 'The extension must be one of the following: ' + extensions.join(', ')
    }
  }

  const valuesMatch = (valueToMatch: string, errorMessage = "Values don't match") => {
    return (value: string) => value === valueToMatch || errorMessage
  }

  const isNotEmpty = (field: string) => {
    return (value: string) => value.length > 0 || `Please select at least one ${field}`
  }

  return {
    required,
    hasLettersOrNumbers,
    hasSearchCharactersOrEmpty,
    maxLength,
    minLength,
    positive,
    greaterThan,
    email,
    isNotEmail,
    validFullUrl,
    notNumbersOnly,
    validUrl,
    allowedExtensions,
    valuesMatch,
    isNotEmpty,
    isAlphanumeric,
    hasNoWhitespaces,
    URL_REGEX,
    EMAIL_REGEX
  }
}

export default useRules
